import { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { MapPin, Phone, Mail, Send, Loader2, ArrowRight } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { COMPANY, SERVICE_INTERESTS } from "@/lib/sipl-content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Contact() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    organization: "",
    service_interest: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);

  const update = (k) => (e) =>
    setForm((f) => ({ ...f, [k]: e.target ? e.target.value : e }));

  const validate = () => {
    if (form.name.trim().length < 2) return "Please enter your name.";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email))
      return "Please enter a valid email.";
    if (!form.service_interest) return "Please choose a service interest.";
    if (form.message.trim().length < 10)
      return "Message should be at least 10 characters.";
    return null;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    const err = validate();
    if (err) {
      toast.error(err);
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Thank you. SIPL team will reach out within 1 business day.");
      setForm({
        name: "",
        email: "",
        phone: "",
        organization: "",
        service_interest: "",
        message: "",
      });
    } catch (e2) {
      toast.error(
        e2?.response?.data?.detail ||
          "Could not submit right now. Please try again or call us."
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      {/* CTA STRIP */}
      <section
        id="cta"
        data-testid="cta-section"
        className="py-16 md:py-24 bg-sipl-secondary relative overflow-hidden"
      >
        <div className="absolute inset-0 opacity-[0.08] [background-image:radial-gradient(white_1px,transparent_1px)] [background-size:18px_18px]" />
        <div className="container-x relative grid grid-cols-1 md:grid-cols-12 items-center gap-8">
          <div className="md:col-span-8">
            <div className="text-sm uppercase tracking-[0.22em] text-white/80 mb-3 dash-accent">
              Partnerships
            </div>
            <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl text-white font-semibold tracking-tight leading-[1.08]">
              Partner with SIPL for sustainable growth.
            </h2>
          </div>
          <div className="md:col-span-4 flex flex-col sm:flex-row md:flex-col gap-3 md:items-end">
            <Button
              onClick={() =>
                document
                  .querySelector("#contact-form")
                  ?.scrollIntoView({ behavior: "smooth" })
              }
              data-testid="cta-get-quote"
              className="bg-sipl-accent hover:bg-sipl-accent/90 text-white h-12 px-7 rounded-md w-full md:w-auto"
            >
              Get a Quote <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
            <Button
              variant="outline"
              onClick={() =>
                document
                  .querySelector("#contact")
                  ?.scrollIntoView({ behavior: "smooth" })
              }
              data-testid="cta-contact-us"
              className="border-white/70 bg-white/0 text-white hover:bg-white hover:text-sipl-secondary h-12 px-7 rounded-md w-full md:w-auto"
            >
              Contact Us
            </Button>
          </div>
        </div>
      </section>

      {/* CONTACT */}
      <section
        id="contact"
        data-testid="contact-section"
        className="section-y bg-white"
      >
        <div className="container-x grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
          <div className="lg:col-span-5">
            <div className="text-sm uppercase tracking-[0.22em] text-sipl-secondary mb-4 dash-accent">
              Contact
            </div>
            <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl text-sipl-primary font-semibold tracking-tight leading-[1.08]">
              Talk to the SIPL team.
            </h2>
            <p className="mt-5 text-sipl-fg/70 max-w-md">
              Tell us about your project — DPR, EPC, O&amp;M, MSW or rural
              infrastructure. We respond within one business day.
            </p>

            <ul className="mt-8 space-y-5">
              <li className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-md bg-sipl-primary/10 text-sipl-primary flex items-center justify-center shrink-0">
                  <MapPin className="w-5 h-5" />
                </div>
                <div>
                  <div className="text-xs uppercase tracking-[0.2em] text-sipl-muted mb-1">
                    Headquarters
                  </div>
                  <div className="text-sipl-fg/85 leading-relaxed">
                    {COMPANY.address}
                  </div>
                </div>
              </li>
              <li className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-md bg-sipl-secondary/10 text-sipl-secondary flex items-center justify-center shrink-0">
                  <Phone className="w-5 h-5" />
                </div>
                <div>
                  <div className="text-xs uppercase tracking-[0.2em] text-sipl-muted mb-1">
                    Phone
                  </div>
                  <a
                    href={`tel:${COMPANY.phone.replace(/\s/g, "")}`}
                    className="text-sipl-fg/85 hover:text-sipl-primary"
                    data-testid="contact-phone"
                  >
                    {COMPANY.phone}
                  </a>
                </div>
              </li>
              <li className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-md bg-sipl-accent/15 text-sipl-accent flex items-center justify-center shrink-0">
                  <Mail className="w-5 h-5" />
                </div>
                <div>
                  <div className="text-xs uppercase tracking-[0.2em] text-sipl-muted mb-1">
                    Email
                  </div>
                  <a
                    href={`mailto:${COMPANY.email}`}
                    className="text-sipl-fg/85 hover:text-sipl-primary"
                    data-testid="contact-email"
                  >
                    {COMPANY.email}
                  </a>
                </div>
              </li>
            </ul>

            <div className="mt-8 rounded-lg overflow-hidden border border-sipl-line">
              <iframe
                title="SIPL HQ Map"
                src="https://www.google.com/maps?q=Max+Towers,+Sector+16B,+Noida&output=embed"
                width="100%"
                height="240"
                style={{ border: 0 }}
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
          </div>

          <motion.form
            id="contact-form"
            data-testid="contact-form"
            onSubmit={onSubmit}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.55 }}
            className="lg:col-span-7 sipl-card p-6 md:p-8 lg:p-10 grid grid-cols-1 md:grid-cols-2 gap-5"
          >
            <div className="md:col-span-1">
              <Label htmlFor="name" className="text-sipl-primary">Full Name *</Label>
              <Input
                id="name"
                value={form.name}
                onChange={update("name")}
                placeholder="e.g. Anita Kumar"
                data-testid="form-name"
                className="mt-2 h-11 border-sipl-line focus-visible:ring-sipl-primary"
              />
            </div>
            <div className="md:col-span-1">
              <Label htmlFor="email" className="text-sipl-primary">Work Email *</Label>
              <Input
                id="email"
                type="email"
                value={form.email}
                onChange={update("email")}
                placeholder="name@organisation.in"
                data-testid="form-email"
                className="mt-2 h-11 border-sipl-line focus-visible:ring-sipl-primary"
              />
            </div>
            <div>
              <Label htmlFor="phone" className="text-sipl-primary">Phone</Label>
              <Input
                id="phone"
                value={form.phone}
                onChange={update("phone")}
                placeholder="+91 9XXXXXXXXX"
                data-testid="form-phone"
                className="mt-2 h-11 border-sipl-line focus-visible:ring-sipl-primary"
              />
            </div>
            <div>
              <Label htmlFor="organization" className="text-sipl-primary">Organisation</Label>
              <Input
                id="organization"
                value={form.organization}
                onChange={update("organization")}
                placeholder="Company / ULB / PSU"
                data-testid="form-organization"
                className="mt-2 h-11 border-sipl-line focus-visible:ring-sipl-primary"
              />
            </div>
            <div className="md:col-span-2">
              <Label className="text-sipl-primary">Service Interest *</Label>
              <Select
                value={form.service_interest}
                onValueChange={(v) =>
                  setForm((f) => ({ ...f, service_interest: v }))
                }
              >
                <SelectTrigger
                  data-testid="form-service-interest"
                  className="mt-2 h-11 border-sipl-line"
                >
                  <SelectValue placeholder="Select a service" />
                </SelectTrigger>
                <SelectContent>
                  {SERVICE_INTERESTS.map((s) => (
                    <SelectItem key={s} value={s} data-testid={`service-option-${s}`}>
                      {s}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="md:col-span-2">
              <Label htmlFor="message" className="text-sipl-primary">Project Brief *</Label>
              <Textarea
                id="message"
                rows={5}
                value={form.message}
                onChange={update("message")}
                placeholder="Tell us about scope, timeline, location, expected capacity..."
                data-testid="form-message"
                className="mt-2 border-sipl-line focus-visible:ring-sipl-primary resize-none"
              />
            </div>
            <div className="md:col-span-2 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 pt-2">
              <p className="text-xs text-sipl-muted">
                By submitting, you agree to be contacted by SIPL regarding your enquiry.
              </p>
              <Button
                type="submit"
                disabled={submitting}
                data-testid="form-submit"
                className="bg-sipl-primary hover:bg-sipl-primary700 text-white h-12 px-7 rounded-md"
              >
                {submitting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Sending...
                  </>
                ) : (
                  <>
                    Send Enquiry <Send className="w-4 h-4 ml-2" />
                  </>
                )}
              </Button>
            </div>
          </motion.form>
        </div>
      </section>
    </>
  );
}
